import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, ShieldCheck } from "lucide-react";
import { lineParent, lineChild, EASE } from "@/lib/motion";
import { LogoMark } from "@/components/Logo";
import { BOOKING_URL } from "@/lib/api";

const BADGES = [
  "Licensed PI",
  "Ghana-Based",
  "100% Independent",
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1599393921693-857c47cf53c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxBY2NyYSUyMEdoYW5hJTIwc2t5bGluZXxlbnwwfHx8fDE3ODU1NzIwNTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollNext = () => {
    const el = document.querySelector("#problem");
    if (el && lenis) lenis.scrollTo(el, { offset: -40 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen bg-navy-deep grain overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Parallax background image with heavy navy overlay */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMG}
          alt="Accra, Ghana city skyline at dusk"
          className="w-full h-full object-cover opacity-25"
        />
      </motion.div>
      <div className="absolute inset-0 z-0 hero-radial" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-navy-deep/40" />

      {/* Faint map / grid line accent */}
      <div className="absolute right-0 top-1/4 z-0 hidden md:block opacity-[0.14]">
        <div className="w-[520px] h-[520px] rounded-full border border-gold" />
        <div className="w-[380px] h-[380px] rounded-full border border-gold absolute top-[70px] left-[70px]" />
        <div className="w-[240px] h-[240px] rounded-full border border-gold absolute top-[140px] left-[140px]" />
      </div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full"
      >
        {/* Animated soundwave motif */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-8"
        >
          <LogoMark size={56} animated />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-8 bg-gold" />
          <span className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold text-gold">
            Licensed Private Investigation · Accra, Ghana
          </span>
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="visible"
          className="font-display font-bold text-white leading-[1.02] tracking-tight text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
          data-testid="hero-headline"
        >
          <span className="line-mask">
            <motion.span variants={lineChild} className="block">
              Before You Trust.
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span variants={lineChild} className="block">
              Before You Invest.
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span
              variants={lineChild}
              className="block italic text-gold"
            >
              VERIFY.
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Your eyes and ears on the ground in Ghana. Whether you're investing
          from abroad, relocating your family, or need something verified locally
          — we investigate the facts so you can make an informed decision.
        </motion.p>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3"
          data-testid="hero-trust-badges"
        >
          {BADGES.map((b) => (
            <div key={b} className="flex items-center gap-2 text-white/80 text-sm">
              <ShieldCheck size={16} className="text-gold" />
              <span className="font-medium tracking-wide">{b}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <span className="font-medium tracking-wide">
              Serving Clients in 🇺🇸 US · 🇬🇧 UK · 🇨🇦 Canada · 🇬🇭 Ghana
            </span>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-3 bg-gold text-navy-deep hover:bg-gold-light transition-colors duration-300 font-semibold px-8 py-4 text-sm uppercase tracking-wider"
          >
            Book Your Free Consultation
            <span className="transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </a>
          <button
            onClick={scrollNext}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 text-white/85 hover:text-gold transition-colors duration-300 font-medium text-sm"
          >
            See How It Works
            <ArrowDown size={16} className="animate-bounce" />
          </button>
        </motion.div>

        {/* Signature phrase */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 font-display italic text-white/40 text-lg"
        >
          “Trust your gut. Then verify it.”
        </motion.p>
      </motion.div>
    </section>
  );
}
